// LineOrder.tsx — moving a purchase order line up or down the document.
//
// One mechanism for both places a buyer arranges lines: the New purchase order
// form (its own <table>) and the detail page's edit mode (a DataTable). Both
// render a <tr> per line; neither hands the row element out. So the grip is a
// CELL: it finds the row it sits in and makes that the sortable node, and the
// row's motion is written to the element directly rather than through props
// the two tables would each have to plumb.
//
// The order itself lives with the caller (an array of ids in state). This file
// only says "id moved from here to there".
//
// dnd-kit is already in the tree (the day planner and Node Plans use the same
// sortable), with the house sensors copied from Node Plans: a 6px pointer
// threshold so a click on the grip is a click, a touch delay so a scroll is a
// scroll, and the keyboard sensor for Space + arrows. On top of that the grip
// answers a bare arrow key with a one-step move, no pick-up: the quickest
// keyboard path, and the one a test can drive without layout.

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type SensorDescriptor,
  type SensorOptions,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconButton } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';

/** A row can only travel up or down the list. */
const VERTICAL_ONLY = [
  ({ transform }: { transform: { x: number; y: number; scaleX: number; scaleY: number } }) => ({ ...transform, x: 0 }),
];

const NO_SENSORS: SensorDescriptor<SensorOptions>[] = [];

/** The house keyboard-selection look, borrowed for the row being carried. */
export const CARRIED_ROW = 'bg-surface-container-highest shadow-[inset_3px_0_0_0_var(--color-primary,#ffcc33)]';

interface LineOrderProps {
  /** Off, the children render with no drag context at all, so a read-only
   * table pays nothing for the grip it is not showing. */
  enabled?: boolean;
  /** Ids in their current order. */
  ids: string[];
  /** What a screen reader calls a line: "Line 3", or the item's name. */
  labelFor: (id: string) => string;
  onMove: (fromIndex: number, toIndex: number) => void;
  /** The row under the pointer, so the caller can tint it. Null when dropped. */
  onCarry?: (id: string | null) => void;
  children: React.ReactNode;
}

export const LineOrder: React.FC<LineOrderProps> = ({ enabled = true, ids, labelFor, onMove, onCarry, children }) => {
  // The context stays mounted whether or not dragging is on. Swapping it for a
  // fragment would remount everything under it, and the detail page has
  // already put focus on its Lines heading by the time edit mode opens.
  // Disabled means no sensors: nothing can start a drag.
  const allSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const sensors = enabled ? allSensors : NO_SENSORS;

  const announcements = useMemo<Announcements>(() => {
    const name = (id: string | number) => labelFor(String(id));
    const where = (id: string | number) => `position ${ids.indexOf(String(id)) + 1} of ${ids.length}`;
    return {
      onDragStart: ({ active }) => `Picked up ${name(active.id)}, ${where(active.id)}.`,
      onDragOver: ({ active, over }) =>
        over ? `${name(active.id)} moved to ${where(over.id)}.` : `${name(active.id)} is not over a place in this list.`,
      onDragEnd: ({ active, over }) =>
        over ? `${name(active.id)} dropped at ${where(over.id)}.` : `${name(active.id)} dropped where it was.`,
      onDragCancel: ({ active }) => `Move cancelled. ${name(active.id)} stays where it was.`,
    };
  }, [ids, labelFor]);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    onCarry?.(null);
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onMove(from, to);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={VERTICAL_ONLY}
      accessibility={{ announcements }}
      onDragStart={({ active }) => onCarry?.(String(active.id))}
      onDragCancel={() => onCarry?.(null)}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
};

interface LineGripProps {
  id: string;
  label: string;
  /** A bare arrow key on the grip: one step, no pick-up. */
  onStep: (delta: -1 | 1) => void;
  disabled?: boolean;
}

/**
 * The grip cell. Renders the transparent icon button the house uses for every
 * icon action, and turns the <tr> it lives in into the sortable node.
 *
 * The row's transform is written to the element in an effect rather than as a
 * style prop, because this component does not render the row. Cleared on
 * unmount and whenever the drag ends, so a row at rest carries no inline style.
 */
export const LineGrip: React.FC<LineGripProps> = ({ id, label, onStep, disabled }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  const rowRef = useRef<HTMLTableRowElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  // A step moves this row's DOM node (React moves the earlier sibling on a
  // swap), and a moved node drops focus to <body>. Set before the step, read
  // after the commit, so the grip that was pressed is the grip that has focus.
  const refocus = useRef(false);
  useEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    buttonRef.current?.focus();
  });

  const attach = useCallback(
    (el: HTMLButtonElement | null) => {
      buttonRef.current = el;
      setActivatorNodeRef(el);
      const row = el ? el.closest('tr') : null;
      rowRef.current = row;
      setNodeRef(row);
    },
    [setNodeRef, setActivatorNodeRef]
  );

  // As a string, because the sortable strategy hands every row a fresh
  // transform object on each frame of somebody else's drag; compared by value,
  // an unmoved row's effect never re-fires.
  const transformCss = CSS.Transform.toString(transform) || '';
  useEffect(() => {
    const row = rowRef.current;
    if (!row) return undefined;
    row.style.transform = transformCss;
    row.style.transition = transition || '';
    row.style.position = isDragging ? 'relative' : '';
    row.style.zIndex = isDragging ? '1' : '';
    return () => {
      row.style.transform = '';
      row.style.transition = '';
      row.style.position = '';
      row.style.zIndex = '';
    };
  }, [transformCss, transition, isDragging]);

  return (
    <Tooltip label={`Move ${label}: drag, or use the arrow keys`}>
    <IconButton
      size="sm"
      ref={attach}
      {...attributes}
      {...listeners}
      disabled={disabled}
      label={`Move ${label}: drag, or use the arrow keys`}
      onKeyDown={(e) => {
        if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') {
          listeners?.onKeyDown?.(e);
          return;
        }
        // Picked up with Space: the arrows belong to the keyboard sensor, which
        // listens on the document, so they must bubble untouched.
        if (isDragging) return;
        e.preventDefault();
        e.stopPropagation();
        refocus.current = true;
        onStep(e.key === 'ArrowUp' ? -1 : 1);
      }}
      className="cursor-grab active:cursor-grabbing disabled:cursor-default touch-none"
    >
      <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor" aria-hidden>
        <circle cx="2.5" cy="2" r="1.3" />
        <circle cx="7.5" cy="2" r="1.3" />
        <circle cx="2.5" cy="7" r="1.3" />
        <circle cx="7.5" cy="7" r="1.3" />
        <circle cx="2.5" cy="12" r="1.3" />
        <circle cx="7.5" cy="12" r="1.3" />
      </svg>
    </IconButton>
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// Charges sink
// ---------------------------------------------------------------------------
//
// Freight and tax are not things the vendor packs, so they print under the
// goods, always. The server settles that on every save (normalizeLineOrder in
// purchase-orders-service.js); these two keep the page honest BEFORE the save,
// so a line never appears somewhere the save would not leave it. `sinks` names
// the lines that live at the bottom.

/** A new line, put where the save would put it: a sinking line at the end,
 * anything else just above the first sinking line. */
export function insertPinned<T>(list: T[], item: T, sinks: (t: T) => boolean): T[] {
  if (sinks(item)) return [...list, item];
  const at = list.findIndex(sinks);
  return at < 0 ? [...list, item] : [...list.slice(0, at), item, ...list.slice(at)];
}

/** The array with one element carried from one index to another, clamped to
 * its own block: a floating line cannot land below a sinking one, nor a
 * sinking line above a floating one. */
export function movePinned<T>(list: T[], from: number, to: number, sinks: (t: T) => boolean): T[] {
  if (from < 0 || from >= list.length) return list;
  const item = list[from];
  const rest = list.filter((_, i) => i !== from);
  const floating = rest.filter((t) => !sinks(t)).length;
  const clamped = sinks(item) ? Math.max(to, floating) : Math.min(to, floating);
  const bounded = Math.max(0, Math.min(clamped, rest.length));
  if (bounded === from) return list;
  return [...rest.slice(0, bounded), item, ...rest.slice(bounded)];
}
